import { FC, useEffect, useState, useContext, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Table, Modal, Select } from "antd";
import { ColumnsType } from "antd/es/table";
import moment from "moment";
import { PlusIcon } from "src/components/icons/plus-icon";
import { EditIcon } from "src/components/icons/edit-icon";
import { DeleteIcon } from "src/components/icons/delete-icon";
import { ResetIcon } from "src/components/icons/reset-icon";
import { EyeIcon } from "src/components/icons/eye-icon";
import { EyeCloseIcon } from "src/components/icons/eye-close-icon";
import Button from "src/components/ui/button";
import ToggleSwitch from "src/components/ui/switch";
import SearchFilter_1 from "src/components/search-filters/user-management-filter_1";
import WhiteLabelAdminDetails from "src/components/white-label-admin/white-label-admin-details";
import client from "src/api";
import routes from "src/config/routes";
import {
  WhiteLabelAdminItem,
  ActiveChannelPartnerItem,
  RoleApiItem,
} from "src/api/types";
import GlobalContext from "src/context/global-context";
import {
  capitalizeEachFirstLetter,
  capitalizeFirstLetter,
  getErrorMessage,
} from "src/lib/utils";
import {
  APISomethingWrongMsg,
  NetworkDisconnectMsg,
  LIST_PAGE_SIZE,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";

const searchFilterOptions = [
  { code: "first_name", name: "First Name" },
  { code: "last_name", name: "Last Name" },
  { code: "email", name: "Email" },
];

const AdminManageWhiteLabelPage: FC = () => {
  const { canView, canList, canAdd, canEdit, canBlock, canDelete } = usePermissions(
    permissionConstants.ADMIN_MANAGEMENT
  );
  const canViewAdmins = canView || canList;

  const { setAlertText, setIsAlertOpened } = useContext(GlobalContext);

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  const { id } = params;
  const currentAdminId: number = id ? Number(id) : -1;

  // Selected Admin State for Details View
  const [selectedAdminId, setSelectedAdminId] = useState<number>(currentAdminId);
  const [selectedAdminData, setSelectedAdminData] = useState<WhiteLabelAdminItem | null>(null);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);

  // Table Data State
  const [admins, setAdmins] = useState<WhiteLabelAdminItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Dropdown Reference Data
  const [channelPartners, setChannelPartners] = useState<ActiveChannelPartnerItem[]>([]);
  const [roles, setRoles] = useState<RoleApiItem[]>([]);
  // Cached active roles mapped by channel partner ID: { [channelPartnerId]: RoleApiItem[] }
  const [rolesCache, setRolesCache] = useState<Record<number, RoleApiItem[]>>({});
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Pagination & Filtering State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(LIST_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchField, setSearchField] = useState("first_name");

  // Create / Edit Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<WhiteLabelAdminItem | null>(null);

  // Delete Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingAdmin, setDeletingAdmin] = useState<WhiteLabelAdminItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [channelPartnerId, setChannelPartnerId] = useState<number | null>(null);
  const [roleId, setRoleId] = useState<number | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isDifferentChannelAccess, setIsDifferentChannelAccess] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [updatingAdminId, setUpdatingAdminId] = useState<number | null>(null);
  const [updating2faAdminId, setUpdating2faAdminId] = useState<number | null>(null);

  // Sync selected admin from URL parameter
  useEffect(() => {
    if (id && canView) {
      const parsedId = Number(id);
      setSelectedAdminId(parsedId);
      fetchAdminDetails(parsedId);
    } else if (id && !canView) {
      setSelectedAdminId(-1);
      setSelectedAdminData(null);
      navigate(`/admin-manage-white-label`, { replace: true });
    } else {
      setSelectedAdminId(-1);
      setSelectedAdminData(null);
    }
  }, [id, canView]);

  // Debounce search input and reset page to 1
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Initial reference data fetch
  useEffect(() => {
    fetchChannelPartners();
    fetchRoles();
  }, []);

  const fetchChannelPartners = async () => {
    try {
      const list = await client.permissions.getActiveChannelPartners();
      setChannelPartners(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to load channel partners", err);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await client.permissions.getRoles(1, 100);
      const safeData = Array.isArray(res.data) ? res.data : [];
      setRoles(safeData);
    } catch (err) {
      console.error("Failed to load roles", err);
    }
  };

  // Helper to fetch and cache active roles for a given channel partner
  const fetchActiveRolesForPartner = async (
    cpId: number,
    forceRefresh = false
  ): Promise<RoleApiItem[]> => {
    if (!cpId) return [];
    if (!forceRefresh && rolesCache[cpId]) {
      return rolesCache[cpId];
    }
    setLoadingRoles(true);
    try {
      const list = await client.permissions.getActiveRoles(cpId);
      const safeList = Array.isArray(list) ? list : [];
      setRolesCache((prev) => ({
        ...prev,
        [cpId]: safeList,
      }));
      return safeList;
    } catch (err) {
      console.error("Failed to load active roles for channel partner", cpId, err);
      return [];
    } finally {
      setLoadingRoles(false);
    }
  };

  // Map for fast lookups
  const channelPartnersMap = useMemo(() => {
    const map: Record<number, string> = {};
    channelPartners.forEach((cp) => {
      map[cp.id] = cp.name;
    });
    return map;
  }, [channelPartners]);

  // Map for fast lookups (combines global roles, all cached active roles, and table data)
  const rolesMap = useMemo(() => {
    const map: Record<number, string> = {};
    roles.forEach((r) => {
      map[r.id] = r.name;
    });
    Object.values(rolesCache).forEach((roleList) => {
      roleList.forEach((r) => {
        map[r.id] = r.name;
      });
    });
    admins.forEach((adm) => {
      if (adm.role?.id && adm.role?.name) {
        map[adm.role.id] = adm.role.name;
      }
    });
    return map;
  }, [roles, rolesCache, admins]);

  // Active roles for the currently selected channel partner in modal
  const modalActiveRoles = useMemo(() => {
    if (!channelPartnerId) return [];
    return rolesCache[channelPartnerId] || [];
  }, [channelPartnerId, rolesCache]);

  const fetchAdmins = async (
    page: number,
    limit: number,
    search?: string,
    field: string = "first_name"
  ) => {
    setLoading(true);
    try {
      const res = await client.whiteLabelAdmin.getAdmins(page, limit, search, field);
      const safeData = Array.isArray(res.data) ? res.data : [];
      setAdmins(safeData);
      setTotalItems(res.total || safeData.length);
    } catch (err: any) {
      setAdmins([]);
      setTotalItems(0);
      if (err?.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else if (err?.response?.data) {
        setAlertText(getErrorMessage(err.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }
      setIsAlertOpened(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminDetails = async (adminId: number) => {
    if (!adminId || adminId <= 0) return;
    setLoadingDetails(true);
    try {
      const details = await client.whiteLabelAdmin.getAdminById(adminId);
      if (details) {
        setSelectedAdminData(details);
        // Pre-cache active roles for partner if channel_partner_id exists
        const partnerId = details.channel_partner_id || details.channel_partner?.id;
        if (partnerId) {
          fetchActiveRolesForPartner(partnerId);
        }
      }
    } catch (err: any) {
      console.error("Failed to fetch admin details", err);
      const errMsg = getErrorMessage(err?.response?.data || err?.data || err);
      if (err?.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else {
        setAlertText(errMsg);
      }
      setIsAlertOpened(true);
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (selectedAdminId <= 0 && canViewAdmins) {
      fetchAdmins(currentPage, pageSize, debouncedSearch, searchField);
    }
  }, [currentPage, pageSize, debouncedSearch, searchField, selectedAdminId, canViewAdmins]);

  const onPageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page);
    setPageSize(newPageSize);
  };

  const handleSearchFieldChange = (newField: string) => {
    setSearchField(newField);
    setSearchQuery("");
    setDebouncedSearch("");
    setCurrentPage(1);
  };

  const handleChannelPartnerChange = (val: number | null) => {
    setChannelPartnerId(val);
    setRoleId(null); // Reset role selection when channel partner changes
    if (Number(val) !== 1) {
      setIsDifferentChannelAccess(false);
    }
    if (val) {
      fetchActiveRolesForPartner(val);
    }
  };

  const onSelectAdmin = (adminId: number) => {
    if (!canView) return;
    setSelectedAdminId(adminId);
    // Find cached admin data from table first for instant render
    const cached = admins.find((a) => a.id === adminId);
    if (cached) {
      setSelectedAdminData(cached);
    }
    navigate(`/admin-manage-white-label/${adminId}`, { replace: true });
    fetchAdminDetails(adminId);
  };

  const onBackFromDetails = () => {
    setSelectedAdminId(-1);
    setSelectedAdminData(null);
    navigate(`/admin-manage-white-label`, { replace: true });
    fetchAdmins(currentPage, pageSize, debouncedSearch, searchField);
  };

  const handleToggleAdminStatus = async (admin: WhiteLabelAdminItem, newStatus: boolean) => {
    if (!canBlock) return;
    setUpdatingAdminId(admin.id);
    try {
      const res = await client.whiteLabelAdmin.updateAdmin(admin.id, {
        is_active: newStatus,
      });

      if (res?.status === "failure" || (res?.code && res.code >= 400)) {
        setAlertText(getErrorMessage(res));
        setIsAlertOpened(true);
        return;
      }

      setAdmins((prevAdmins) =>
        prevAdmins.map((a) =>
          a.id === admin.id ? { ...a, is_active: newStatus } : a
        )
      );
      if (selectedAdminId === admin.id) {
        setSelectedAdminData((prev) => (prev ? { ...prev, is_active: newStatus } : prev));
      }
      const adminFullName = `${admin.first_name || ""} ${admin.last_name || ""}`.trim() || `Admin #${admin.id}`;
      setAlertText(`Admin "${adminFullName}" status updated successfully!`);
      setIsAlertOpened(true);
    } catch (error: any) {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else if (error.response?.data) {
        setAlertText(getErrorMessage(error.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }
      setIsAlertOpened(true);
    } finally {
      setUpdatingAdminId(null);
    }
  };

  const handleToggle2fa = async (admin: WhiteLabelAdminItem, newStatus: boolean) => {
    if (!canBlock) {
      setAlertText("You do not have permission to update 2FA status");
      setIsAlertOpened(true);
      return;
    }
    if (newStatus) {
      setAlertText("Google 2FA can only be disabled, not enabled");
      setIsAlertOpened(true);
      return;
    }
    if (!admin?.id || updating2faAdminId === admin.id) return;

    setUpdating2faAdminId(admin.id);
    try {
      const res = await client.whiteLabelAdmin.updateAdmin(admin.id, {
        google2fa_enabled: false,
      });

      if (res?.status === "failure" || (res?.code && res.code >= 400)) {
        setAlertText(getErrorMessage(res));
        setIsAlertOpened(true);
        return;
      }

      setAdmins((prevAdmins) =>
        prevAdmins.map((a) =>
          a.id === admin.id ? { ...a, google2fa_enabled: false } : a
        )
      );
      if (selectedAdminId === admin.id) {
        setSelectedAdminData((prev) => (prev ? { ...prev, google2fa_enabled: false } : prev));
      }
      const adminFullName = `${admin.first_name || ""} ${admin.last_name || ""}`.trim() || `Admin #${admin.id}`;
      setAlertText(`Admin "${adminFullName}" Google 2FA disabled successfully!`);
      setIsAlertOpened(true);
    } catch (error: any) {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else if (error.response?.data) {
        setAlertText(getErrorMessage(error.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }
      setIsAlertOpened(true);
    } finally {
      setUpdating2faAdminId(null);
    }
  };

  const handleOpenAddModal = () => {
    if (!canAdd) return;
    setEditingItem(null);
    setChannelPartnerId(null);
    setRoleId(null);
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setIsDifferentChannelAccess(false);
    setIsModalOpen(true);
    if (channelPartners.length === 0) fetchChannelPartners();
  };

  const handleOpenEditModal = async (item: WhiteLabelAdminItem) => {
    if (!canEdit) return;
    const cpId = item.channel_partner_id || item.channel_partner?.id || item.channelPartner?.id || null;
    const rId = item.role_id || item.role?.id || null;

    setEditingItem(item);
    setChannelPartnerId(cpId);
    setRoleId(rId);
    setFirstName(item.first_name || "");
    setLastName(item.last_name || "");
    setEmail(item.email || "");
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setIsDifferentChannelAccess(
      Number(cpId) === 1 ? Boolean(item.is_different_channel_access) : false
    );
    setIsModalOpen(true);
    if (channelPartners.length === 0) fetchChannelPartners();

    if (cpId) {
      fetchActiveRolesForPartner(cpId);
    }

    // Optionally fetch full single item details
    try {
      const details = await client.whiteLabelAdmin.getAdminById(item.id);
      if (details) {
        const freshCpId = details.channel_partner_id || details.channel_partner?.id || cpId;
        const freshRoleId = details.role_id || details.role?.id || rId;

        if (freshCpId) {
          setChannelPartnerId(freshCpId);
          fetchActiveRolesForPartner(freshCpId);
        }
        if (freshRoleId) {
          setRoleId(freshRoleId);
        }
        if (details.first_name) setFirstName(details.first_name);
        if (details.last_name) setLastName(details.last_name);
        if (details.email) setEmail(details.email);
        if (typeof details.is_different_channel_access === "boolean") {
          setIsDifferentChannelAccess(
            Number(freshCpId) === 1 ? Boolean(details.is_different_channel_access) : false
          );
        }
      }
    } catch (e) {
      console.error("Failed to fetch fresh admin details", e);
    }
  };

  const handleOpenDeleteModal = (admin: WhiteLabelAdminItem) => {
    if (!canDelete) {
      setAlertText("You do not have permission to delete admins");
      setIsAlertOpened(true);
      return;
    }
    setDeletingAdmin(admin);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!canDelete || !deletingAdmin?.id || deleteLoading) return;

    setDeleteLoading(true);
    try {
      const res = await client.whiteLabelAdmin.deleteAdmin(deletingAdmin.id);

      if (res?.status === "failure" || (res?.code && res.code >= 400)) {
        setAlertText(getErrorMessage(res));
        setIsAlertOpened(true);
        return;
      }

      const adminFullName =
        `${deletingAdmin.first_name || ""} ${deletingAdmin.last_name || ""}`.trim() ||
        `Admin #${deletingAdmin.id}`;
      setAlertText(res?.message || `Admin "${adminFullName}" deleted successfully.`);
      setIsAlertOpened(true);

      setIsDeleteModalOpen(false);
      const deletedId = deletingAdmin.id;
      setDeletingAdmin(null);

      if (selectedAdminId === deletedId) {
        setSelectedAdminId(-1);
        setSelectedAdminData(null);
        navigate(routes.adminManageWhiteLabel, { replace: true });
      }

      fetchAdmins(currentPage, pageSize, debouncedSearch, searchField);
    } catch (error: any) {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else if (error.response?.data) {
        setAlertText(getErrorMessage(error.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }
      setIsAlertOpened(true);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingItem && !canEdit) return;
    if (!editingItem && !canAdd) return;

    if (!channelPartnerId) {
      setAlertText("error_Please select a channel partner!");
      setIsAlertOpened(true);
      return;
    }

    if (!roleId) {
      setAlertText("error_Please select a role!");
      setIsAlertOpened(true);
      return;
    }

    if (!firstName.trim()) {
      setAlertText("error_Please enter first name!");
      setIsAlertOpened(true);
      return;
    }

    if (!lastName.trim()) {
      setAlertText("error_Please enter last name!");
      setIsAlertOpened(true);
      return;
    }

    if (!editingItem) {
      if (!email.trim()) {
        setAlertText("error_Please enter email address!");
        setIsAlertOpened(true);
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        setAlertText("error_Please enter a valid email address!");
        setIsAlertOpened(true);
        return;
      }

      if (!password || password.trim().length < 6) {
        setAlertText("error_Password must be at least 6 characters long!");
        setIsAlertOpened(true);
        return;
      }

      if (!confirmPassword) {
        setAlertText("error_Please confirm your password!");
        setIsAlertOpened(true);
        return;
      }

      if (password !== confirmPassword) {
        setAlertText("error_Passwords do not match!");
        setIsAlertOpened(true);
        return;
      }
    }

    setSubmitLoading(true);

    try {
      const isDiffAccess = Number(channelPartnerId) === 1 ? Boolean(isDifferentChannelAccess) : false;

      if (editingItem) {
        const payload: any = {
          channel_partner_id: Number(channelPartnerId),
          role_id: Number(roleId),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          is_different_channel_access: isDiffAccess,
        };

        const res = await client.whiteLabelAdmin.updateAdmin(editingItem.id, payload);
        if (res?.status === "failure" || (res?.code && res.code >= 400)) {
          setAlertText(getErrorMessage(res));
          setIsAlertOpened(true);
          return;
        }
        setAlertText(`Admin "${firstName.trim()} ${lastName.trim()}" updated successfully!`);
        setIsAlertOpened(true);

        // If currently viewing this admin in details page, refresh details
        if (selectedAdminId === editingItem.id) {
          fetchAdminDetails(editingItem.id);
        }
      } else {
        const payload = {
          channel_partner_id: Number(channelPartnerId),
          role_id: Number(roleId),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          password: password.trim(),
          is_different_channel_access: isDiffAccess,
        };

        const res = await client.whiteLabelAdmin.createAdmin(payload);
        if (res?.status === "failure" || (res?.code && res.code >= 400)) {
          setAlertText(getErrorMessage(res));
          setIsAlertOpened(true);
          return;
        }
        setAlertText(`Admin "${firstName.trim()} ${lastName.trim()}" added successfully!`);
        setIsAlertOpened(true);
      }

      setIsModalOpen(false);
      setEditingItem(null);
      setFirstName("");
      setLastName("");
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setShowPassword(false);
      setShowConfirmPassword(false);
      setChannelPartnerId(null);
      setRoleId(null);
      setIsDifferentChannelAccess(false);
      fetchAdmins(currentPage, pageSize, debouncedSearch, searchField);
    } catch (error: any) {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else if (error.response?.data) {
        setAlertText(getErrorMessage(error.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }
      setIsAlertOpened(true);
    } finally {
      setSubmitLoading(false);
    }
  };

  const columns: ColumnsType<WhiteLabelAdminItem> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          ID
        </p>
      ),
      dataIndex: "id",
      key: "id",
      className: "w-auto sm:w-[100px]",
      render: (value: number) => (
        <div className="flex flex-row gap-2 items-center">
          {canView ? (
            <a
              className="font-manrope text-secondary text-[14px] font-medium underline hover:text-primary hover:underline cursor-pointer"
              onClick={() => onSelectAdmin(value)}
              title="View Details"
            >
              {value}
            </a>
          ) : (
            <span className="font-manrope text-secondary text-[14px] font-medium">
              {value}
            </span>
          )}
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Name & Email
        </p>
      ),
      key: "name",
      className: "w-auto min-w-[150px]",
      render: (_, record) => {
        const fullName = `${record.first_name || ""} ${record.last_name || ""}`.trim();
        return (
          <div
            className={`flex flex-col ${canView ? "cursor-pointer group" : ""}`}
            onClick={() => {
              if (canView) onSelectAdmin(record.id);
            }}
            title={canView ? "View Details" : undefined}
          >
            <p className={`font-manrope text-primary text-[14px] font-semibold ${canView ? "group-hover:underline" : ""}`}>
              {fullName ? capitalizeEachFirstLetter(fullName) : "-"}
            </p>
            <p className="font-manrope text-secondary text-[12px]">
              {record.email || "-"}
            </p>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Channel Partner
        </p>
      ),
      key: "channel_partner",
      className: "w-auto",
      render: (_, record) => {
        const name =
          record.channel_partner?.name ||
          record.channelPartner?.name ||
          channelPartnersMap[record.channel_partner_id] ||
          (record.channel_partner_id ? `CP #${record.channel_partner_id}` : "-");
        return (
          <div className="flex flex-row gap-2 items-center">
            <p className="font-manrope text-primary text-[14px] font-medium">
              {capitalizeFirstLetter(name)}
            </p>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Role
        </p>
      ),
      key: "role",
      className: "w-auto",
      render: (_, record) => {
        const roleName =
          record.role?.name ||
          rolesMap[record.role_id] ||
          (record.role_id ? `Role #${record.role_id}` : "-");
        return (
          <div className="flex flex-row gap-2 items-center">
            <p className="font-manrope text-primary text-[14px] font-medium">
              {capitalizeFirstLetter(roleName)}
            </p>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Google 2FA
        </p>
      ),
      dataIndex: "google2fa_enabled",
      key: "google2fa_enabled",
      className: "w-[120px]",
      render: (is2faEnabled: boolean | undefined, record: WhiteLabelAdminItem) => {
        const is2faActive = Boolean(record.google2fa_enabled ?? is2faEnabled);
        const isDisabled = !canBlock || updating2faAdminId === record.id || !is2faActive;

        let tooltipText: string | undefined = undefined;
        if (!canBlock) {
          tooltipText = "Read only - No block permission";
        } else if (!is2faActive) {
          tooltipText = "Google 2FA is disabled (can only be disabled, not enabled)";
        }

        return (
          <div
            className={`flex flex-row gap-2 items-center ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            title={tooltipText}
            onClick={() => {
              if (!canBlock) return;
              if (!is2faActive) {
                setAlertText("Google 2FA can only be disabled, not enabled");
                setIsAlertOpened(true);
              }
            }}
          >
            <ToggleSwitch
              type={`2fa-${record.id}`}
              value={is2faActive}
              disabled={isDisabled}
              onValueChanged={(_: string, newValue: boolean) => {
                if (!canBlock) return;
                if (newValue) {
                  setAlertText("Google 2FA can only be disabled, not enabled");
                  setIsAlertOpened(true);
                  return;
                }
                handleToggle2fa(record, newValue);
              }}
            />
          </div>
        );
      },
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Created At
        </p>
      ),
      dataIndex: "created_at",
      key: "created_at",
      className: "w-auto min-w-[140px]",
      render: (value?: string) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[14px] font-medium">
            {value ? moment(value).format("YYYY-MM-DD HH:mm") : "-"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Status
        </p>
      ),
      dataIndex: "is_active",
      key: "is_active",
      className: "w-[120px]",
      render: (isActive: boolean | undefined, record: WhiteLabelAdminItem) => {
        const isDisabled = !canBlock || updatingAdminId === record.id;
        return (
          <div
            className={`flex flex-row gap-2 items-center ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            title={!canBlock ? "Read only - No block permission" : undefined}
          >
            <ToggleSwitch
              type={`${record.id}`}
              value={!!isActive}
              disabled={isDisabled}
              onValueChanged={(_: string, newValue: boolean) => {
                if (!canBlock) return;
                handleToggleAdminStatus(record, newValue);
              }}
            />
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Action
        </p>
      ),
      key: "actions",
      className: "w-[100px]",
      render: (_, record) => {
        if (!canEdit && !canDelete) {
          return (
            <p className="font-manrope text-gray-400 text-[20px] font-bold text-center">
              —
            </p>
          );
        }
        return (
          <div className="flex flex-row items-center gap-1.5">
            {canEdit && (
              <button
                className="w-[32px] h-[32px] flex items-center justify-center hover:bg-[#E6E8EC] rounded transition-colors"
                onClick={() => handleOpenEditModal(record)}
                title="Edit Admin"
              >
                <EditIcon className="w-[18px] h-[18px] text-primary" />
              </button>
            )}
            {canDelete && (
              <button
                className="w-[32px] h-[32px] flex items-center justify-center hover:bg-[#FEEAEA] rounded transition-colors"
                onClick={() => handleOpenDeleteModal(record)}
                title="Delete Admin"
              >
                <DeleteIcon className="w-[18px] h-[18px] text-error" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  if (!canViewAdmins) {
    return (
      <div className="w-full h-full bg-gray">
        <div className="w-full h-full flex-col p-2 sm:p-4">
          <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
            Manage Admin
          </p>
          <div className="bg-white rounded-[12px] p-6 mt-4 text-center text-secondary">
            You do not have permission to view admins.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gray">
      {selectedAdminId > 0 ? (
        /* Dedicated Details Page View */
        <div className="w-full h-full flex flex-col p-2 sm:p-4">
          <WhiteLabelAdminDetails
            admin={selectedAdminData || admins.find((a) => a.id === selectedAdminId) || null}
            loading={loadingDetails}
            channelPartnersMap={channelPartnersMap}
            rolesMap={rolesMap}
            onBack={onBackFromDetails}
            onEdit={(adm) => handleOpenEditModal(adm)}
            onRefresh={() => fetchAdminDetails(selectedAdminId)}
            onToggleStatus={handleToggleAdminStatus}
            updatingAdminId={updatingAdminId}
            canEdit={canEdit}
            canBlock={canBlock}
          />
        </div>
      ) : (
        /* Table List View */
        <div className="w-full h-full flex-col p-2 sm:p-4">
          {/* Header */}
          <div className="w-full flex flex-row items-center justify-between">
            <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
              Manage Admin
            </p>
            <PermissionGate module={[permissionConstants.ADMIN_MANAGEMENT]} action="can_add">
              <div className="flex flex-row items-center gap-3">
                <button
                  className="flex flex-row items-center justify-between px-2 sm:px-4 py-2 gap-2 bg-white rounded-[6px] flex-shrink-0 hover:bg-[#E6E8EC] transition-colors"
                  onClick={handleOpenAddModal}
                >
                  <p className="font-manrope text-[14px] font-medium text-primary">
                    Add Admin
                  </p>
                  <PlusIcon className="w-[18px] h-[18px] text-primary" />
                </button>
              </div>
            </PermissionGate>
          </div>

          {/* Search & Actions Toolbar */}
          <div className="mt-2 sm:mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <SearchFilter_1
              options={searchFilterOptions}
              placeholder={`Search by ${searchFilterOptions.find((o) => o.code === searchField)?.name.toLowerCase() || "first name"}`}
              value={searchField}
              onChange={handleSearchFieldChange}
              searchText={searchQuery}
              setSearchText={setSearchQuery}
            />

            <div className="flex items-center gap-2">
              <button
                className="flex flex-row items-center justify-between px-3 py-2 gap-2 bg-white rounded-[6px] flex-shrink-0 hover:bg-[#E6E8EC] h-[44px] border border-light-300 transition-colors"
                onClick={() => {
                  fetchAdmins(currentPage, pageSize, debouncedSearch, searchField);
                  fetchChannelPartners();
                  fetchRoles();
                  setRolesCache({});
                }}
              >
                <p className="font-manrope text-[14px] font-medium text-primary">
                  Refresh
                </p>
                <ResetIcon className="w-[16px] h-[16px] text-primary" />
              </button>
            </div>
          </div>

          {/* Admin Table */}
          <div className="flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
            <Table
              loading={loading}
              showSorterTooltip={false}
              columns={columns}
              dataSource={admins}
              pagination={{
                total: totalItems,
                current: currentPage,
                pageSize: pageSize,
                onChange: onPageChange,
                position: ["bottomCenter"],
                defaultPageSize: LIST_PAGE_SIZE,
                showSizeChanger: false,
              }}
              rowKey="id"
            />
          </div>
        </div>
      )}

      {/* Create / Edit Admin Modal */}
      <Modal
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingItem(null);
          setPassword("");
          setConfirmPassword("");
          setShowPassword(false);
          setShowConfirmPassword(false);
        }}
        footer={null}
        title={
          <p className="text-[20px] font-manrope font-medium text-primary">
            {editingItem ? "Edit Admin" : "Add Admin"}
          </p>
        }
      >
        <form onSubmit={handleFormSubmit} className="mt-4 flex flex-col gap-3 font-manrope">
          {/* Channel Partner Dropdown */}
          <div>
            <p className="text-[14px] font-manrope font-medium text-secondary mb-1.5">
              Channel Partner <span className="text-error">*</span>
            </p>
            <Select
              size="large"
              placeholder="Select channel partner"
              value={channelPartnerId || undefined}
              onChange={handleChannelPartnerChange}
              disabled={submitLoading}
              className="w-full font-manrope custom-select"
              popupClassName="custom-select-dropdown"
              options={channelPartners.map((cp) => ({
                value: cp.id,
                label: cp.name,
              }))}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              notFoundContent="No active channel partners found"
            />
          </div>

          {/* Role Dropdown */}
          <div>
            <p className="text-[14px] font-manrope font-medium text-secondary mb-1.5">
              Role <span className="text-error">*</span>
            </p>
            <Select
              size="large"
              placeholder={
                !channelPartnerId
                  ? "Select channel partner first"
                  : loadingRoles
                    ? "Loading active roles..."
                    : "Select role"
              }
              value={roleId || undefined}
              onChange={(val) => setRoleId(val)}
              disabled={!channelPartnerId || loadingRoles || submitLoading}
              loading={loadingRoles}
              className="w-full font-manrope custom-select"
              popupClassName="custom-select-dropdown"
              options={[
                ...(roleId && !modalActiveRoles.some((r) => r.id === roleId)
                  ? [
                    {
                      value: roleId,
                      label:
                        editingItem?.role?.name ||
                        rolesMap[roleId] ||
                        `Role #${roleId}`,
                    },
                  ]
                  : []),
                ...modalActiveRoles.map((r) => ({
                  value: r.id,
                  label: r.name,
                })),
              ]}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              notFoundContent={
                loadingRoles
                  ? "Loading roles..."
                  : !channelPartnerId
                    ? "Please select a channel partner first"
                    : "No active roles found for this channel partner"
              }
            />
          </div>

          {/* First Name & Last Name Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-[14px] font-manrope font-medium text-secondary mb-1.5">
                First Name <span className="text-error">*</span>
              </p>
              <input
                type="text"
                className="h-[48px] placeholder-other w-full appearance-none text-[15px] font-manrope font-medium rounded-[10px] border border-border bg-white px-4 py-2 text-primary focus:border-active focus:ring-0 focus:outline-none"
                placeholder="e.g. John"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div>
              <p className="text-[14px] font-manrope font-medium text-secondary mb-1.5">
                Last Name <span className="text-error">*</span>
              </p>
              <input
                type="text"
                className="h-[48px] placeholder-other w-full appearance-none text-[15px] font-manrope font-medium rounded-[10px] border border-border bg-white px-4 py-2 text-primary focus:border-active focus:ring-0 focus:outline-none"
                placeholder="e.g. Doe"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <p className="text-[14px] font-manrope font-medium text-secondary mb-1.5">
              Email Address <span className="text-error">*</span>
            </p>
            <input
              type="email"
              className={`h-[48px] placeholder-other w-full appearance-none text-[15px] font-manrope font-medium rounded-[10px] border border-border px-4 py-2 text-primary focus:border-active focus:ring-0 focus:outline-none ${editingItem
                ? "bg-[#F3F4F6] text-[#6B7280] cursor-not-allowed border-[#E5E7EB]"
                : "bg-white"
                }`}
              placeholder="e.g. admin@example.com"
              value={email}
              disabled={Boolean(editingItem)}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {/* Password (Only on Create Admin) */}
          {!editingItem && (
            <>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[14px] font-manrope font-medium text-secondary">
                    Password <span className="text-error">*</span>
                  </p>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="h-[48px] placeholder-other w-full appearance-none text-[15px] font-manrope font-medium rounded-[10px] border border-border bg-white px-4 py-2 pr-11 text-primary focus:border-active focus:ring-0 focus:outline-none"
                    placeholder="Enter password (min 6 characters)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-primary"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeCloseIcon className="w-[20px] h-[20px]" />
                    ) : (
                      <EyeIcon className="w-[20px] h-[20px]" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[14px] font-manrope font-medium text-secondary">
                    Confirm Password <span className="text-error">*</span>
                  </p>
                </div>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className="h-[48px] placeholder-other w-full appearance-none text-[15px] font-manrope font-medium rounded-[10px] border border-border bg-white px-4 py-2 pr-11 text-primary focus:border-active focus:ring-0 focus:outline-none"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-primary"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeCloseIcon className="w-[20px] h-[20px]" />
                    ) : (
                      <EyeIcon className="w-[20px] h-[20px]" />
                    )}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Different Channel Access Toggle - only show when selected channel partner id is 1 */}
          {Number(channelPartnerId) === 1 && (
            <div className="flex items-center justify-between p-3 rounded-[10px] bg-[#F8F9FA] border border-border mt-1">
              <div>
                <p className="text-[14px] font-manrope font-semibold text-primary">
                  Different Channel Access
                </p>
                <p className="text-[12px] font-manrope text-secondary">
                  Allow this admin to access different channel partners
                </p>
              </div>
              <ToggleSwitch
                type="modal_diff_access"
                value={isDifferentChannelAccess}
                onValueChanged={(_: string, checked: boolean) =>
                  setIsDifferentChannelAccess(checked)
                }
              />
            </div>
          )}

          {/* Footer Buttons */}
          <div className="w-full flex flex-row items-center justify-end gap-2 mt-4 pt-4 border-t border-light-300">
            <Button
              variant="solid"
              type="button"
              className="w-[120px] rounded-[8px] font-manrope text-[14px] font-bold leading-[20px] h-[44px] bg-[#BCBEAD] hover:bg-light-600"
              onClick={() => {
                setIsModalOpen(false);
                setEditingItem(null);
                setPassword("");
                setConfirmPassword("");
                setShowPassword(false);
                setShowConfirmPassword(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="validate"
              type="submit"
              className="w-[120px] rounded-[8px] font-manrope text-[14px] font-bold leading-[20px] h-[44px]"
              disabled={submitLoading}
              isLoading={submitLoading}
            >
              {editingItem ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Admin Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        onCancel={() => {
          if (!deleteLoading) {
            setIsDeleteModalOpen(false);
            setDeletingAdmin(null);
          }
        }}
        footer={null}
        width={460}
        centered
        destroyOnClose
      >
        <div className="p-4 sm:p-6 flex flex-col items-center justify-center text-center gap-4">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center text-error mb-1">
            <DeleteIcon className="w-7 h-7 text-error" />
          </div>
          <h3 className="font-manrope text-[20px] font-bold text-primary">
            Delete Admin
          </h3>
          <p className="font-manrope text-[15px] text-secondary leading-relaxed">
            Are you sure you want to delete admin{" "}
            <span className="font-semibold text-primary">
              {deletingAdmin
                ? `${deletingAdmin.first_name || ""} ${deletingAdmin.last_name || ""}`.trim() || `Admin #${deletingAdmin.id}`
                : "this admin"}
            </span>
            {deletingAdmin?.email && (
              <span className="block text-gray-500 text-sm mt-1">
                ({deletingAdmin.email})
              </span>
            )}
            ? This action cannot be undone.
          </p>
          <div className="w-full flex flex-row items-center justify-center gap-3 mt-4">
            <Button
              variant="outline"
              type="button"
              className="w-full max-w-[140px] h-[48px] rounded-[10px] font-manrope font-semibold"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeletingAdmin(null);
              }}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              type="button"
              className="w-full max-w-[140px] h-[48px] rounded-[10px] font-manrope font-semibold bg-error hover:bg-opacity-80 text-white"
              onClick={handleConfirmDelete}
              isLoading={deleteLoading}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminManageWhiteLabelPage;
